// role-based-dashboards — Phase 7: super_admin platform overview page.
//
// Snapshot-only (spec "Snapshot-Only Super Admin Overview" — NO polling/
// auto-refresh): a single `getPlatformOverview()` fetch backs org counts/
// distribution, plan distribution, and subscription aggregates + MRR;
// `PlatformAuditFeed` adds a cursor-paginated (user-triggered "load more",
// not a poll) cross-org audit feed. Reuses the existing `BarChart` primitive
// (admin-analytics-expansion) rather than introducing a new chart component.

import { getPlatformOverview } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import { DEFAULT_CURRENCY, type PlatformOverviewResponse } from '@jq4race/shared';
import { formatCents, Skeleton, StatCard } from '@jq4race/ui';
import { useQuery } from '@tanstack/react-query';
import { Building2, CreditCard, TrendingUp, TriangleAlert } from 'lucide-react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { BarChart } from '../../../analytics/ui/components/ChartPrimitives';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { toChartData } from '../../lib/toChartData';
import { PlatformAuditFeed } from '../components/PlatformAuditFeed';

const CURRENCY_FALLBACK = DEFAULT_CURRENCY;

// FIX 1 (review) — MRR must not conflate currencies. The backend already
// groups active-subscription MRR by currency (`mrrByCurrency`), so the UI
// renders one StatCard PER currency instead of hardcoding a single USD
// total. With zero or exactly one currency present, this collapses to the
// same single "MRR" card as before (just without the hardcoded USD
// fallback when a currency IS known).
function renderMrrCards(
  mrrByCurrency: PlatformOverviewResponse['subscriptions']['mrrByCurrency'] | undefined,
  isLoading: boolean,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (isLoading) {
    return <StatCard icon={TrendingUp} label={t('stats.mrr')} value={null} />;
  }
  const entries = mrrByCurrency ?? [];
  if (entries.length === 0) {
    return (
      <StatCard
        icon={TrendingUp}
        label={t('stats.mrr')}
        value={formatCents(0, CURRENCY_FALLBACK)}
      />
    );
  }
  if (entries.length === 1) {
    // `entries[0]` narrows to `... | undefined` under noUncheckedIndexedAccess
    // regardless of the `length === 1` guard above (TS can't correlate array
    // length checks with index-access safety) — fall back to the zero-currency
    // rendering (unreachable in practice, since length is exactly 1 here).
    const only = entries[0];
    if (!only) {
      return (
        <StatCard
          icon={TrendingUp}
          label={t('stats.mrr')}
          value={formatCents(0, CURRENCY_FALLBACK)}
        />
      );
    }
    return (
      <StatCard
        icon={TrendingUp}
        label={t('stats.mrr')}
        value={formatCents(only.amountCents, only.currency)}
      />
    );
  }
  return (
    <>
      {entries.map((entry) => (
        <StatCard
          key={entry.currency}
          icon={TrendingUp}
          label={t('stats.mrrWithCurrency', { currency: entry.currency.toUpperCase() })}
          value={formatCents(entry.amountCents, entry.currency)}
        />
      ))}
    </>
  );
}

export function PlatformOverviewPage() {
  const { t } = useTranslation('super-admin-overview');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['super-admin-overview', 'platform-overview'],
    queryFn: () => getPlatformOverview(),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('header.eyebrow')}
        title={t('header.title')}
        description={t('header.description')}
      />

      {isError ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-4 text-[13px] text-jq-danger"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
          <p className="leading-snug">{translateApiError(error, t, { fallback: t('error') })}</p>
        </div>
      ) : (
        <>
          {/* ----- Top-line stats ----- */}
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              icon={Building2}
              label={t('stats.totalOrganizations')}
              value={isLoading ? null : (data?.orgs.total ?? 0).toString()}
            />
            <StatCard
              icon={CreditCard}
              label={t('stats.activeSubscriptions')}
              value={isLoading ? null : (data?.subscriptions.activeCount ?? 0).toString()}
            />
            {renderMrrCards(data?.subscriptions.mrrByCurrency, isLoading, t)}
          </section>

          {/* ----- Distributions ----- */}
          <section className="grid gap-6 lg:grid-cols-3">
            <DistributionCard
              title={t('distributions.organizationsByStatus')}
              isLoading={isLoading}
              data={toChartData(data?.orgs.byStatus, (raw) => enumLabel(t, 'orgStatus', raw))}
            />
            <DistributionCard
              title={t('distributions.organizationsByNiche')}
              isLoading={isLoading}
              data={toChartData(data?.orgs.byNiche, (raw) => enumLabel(t, 'niche', raw))}
            />
            <DistributionCard
              title={t('distributions.subscriptionsByStatus')}
              isLoading={isLoading}
              data={toChartData(data?.subscriptions.byStatus, (raw) =>
                enumLabel(t, 'subscriptionStatus', raw),
              )}
            />
          </section>

          {/* ----- Plan distribution ----- */}
          <section>
            <p className="jq-eyebrow mb-4">{t('plans.eyebrow')}</p>
            {isLoading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : data && data.plans.byPlan.length > 0 ? (
              <BarChart
                data={data.plans.byPlan.map((p) => ({ name: p.planName, value: p.orgCount }))}
                xKey="name"
                series={[{ dataKey: 'value', label: t('plans.seriesLabel') }]}
                height={220}
              />
            ) : (
              <p className="py-8 text-center text-[13px] text-jq-stone">{t('plans.empty')}</p>
            )}
          </section>

          {/* ----- Cross-org audit feed ----- */}
          <PlatformAuditFeed />
        </>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function DistributionCard({
  title,
  isLoading,
  data,
}: {
  title: string;
  isLoading: boolean;
  data: { name: string; value: number }[];
}) {
  const { t } = useTranslation('super-admin-overview');
  return (
    <div className="rounded-sm border border-jq-sand/70 bg-jq-surface-0 p-4 shadow-sm">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-jq-graphite">
        {title}
      </h3>
      <div className="mt-3">
        {isLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : data.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-jq-stone">{t('distributions.noData')}</p>
        ) : (
          <BarChart
            data={data}
            xKey="name"
            series={[{ dataKey: 'value', label: title }]}
            height={200}
          />
        )}
      </div>
    </div>
  );
}
